import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, LogIn, LayoutDashboard } from "lucide-react";
import { Link } from "react-router-dom";
import { Wordmark } from "./Logo";
import { useAuth } from "../auth/AuthContext";

const links = [
  { href: "#journey", label: "Journey" },
  { href: "#challenges", label: "Challenges" },
  { href: "#methods", label: "Playbook" },
  { href: "#impact", label: "Impact" },
];

export const Navbar = ({ onJoin }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, login } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[color:var(--mati-sand)]/80 border-b border-[color:var(--mati-border)]"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center">
          <Wordmark />
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-sub text-[13px] tracking-wide text-[color:var(--mati-ink)] hover:text-[color:var(--mati-clay)] transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[1.5px] w-0 bg-[color:var(--mati-clay)] group-hover:w-full transition-all duration-500" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <Link
              to="/dashboard"
              data-testid="nav-dashboard-link"
              className="group hidden sm:inline-flex items-center gap-2 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] pl-2 pr-4 py-1.5 rounded-full text-[12px] font-sub font-semibold tracking-wider uppercase hover:bg-[color:var(--mati-clay)] transition-all"
            >
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <span className="w-7 h-7 rounded-full bg-[color:var(--mati-clay)] flex items-center justify-center text-[10px]">
                  {user.name?.[0]?.toUpperCase() || "M"}
                </span>
              )}
              <span className="hidden lg:inline">
                {user.name?.split(" ")[0]}
              </span>
              <LayoutDashboard className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <button
              onClick={login}
              data-testid="nav-signin-btn"
              className="hidden sm:inline-flex items-center gap-2 border border-[color:var(--mati-forest)] text-[color:var(--mati-forest)] px-4 py-2.5 rounded-full text-[12px] font-sub font-semibold tracking-wider uppercase hover:bg-[color:var(--mati-forest)] hover:text-[color:var(--mati-sand)] transition-all"
            >
              <LogIn className="w-3.5 h-3.5" />
              Sign in
            </button>
          )}

          <button
            onClick={onJoin}
            data-testid="nav-join-drive-btn"
            className="group hidden sm:inline-flex items-center gap-2 bg-[color:var(--mati-clay)] text-[color:var(--mati-sand)] px-5 py-2.5 rounded-full text-[12px] font-sub font-semibold tracking-wider uppercase hover:bg-[color:var(--mati-forest)] transition-all"
          >
            Join the Drive
            <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform" />
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 rounded-full border border-[color:var(--mati-border)]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-[color:var(--mati-sand)] border-t border-[color:var(--mati-border)]"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-display text-2xl"
                >
                  {l.label}
                </a>
              ))}
              {user ? (
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-dashboard-link"
                  className="mt-2 self-start bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] px-5 py-2.5 rounded-full text-[12px] font-sub font-semibold tracking-wider uppercase"
                >
                  Dashboard
                </Link>
              ) : (
                <button
                  onClick={() => {
                    setOpen(false);
                    login();
                  }}
                  data-testid="mobile-signin-btn"
                  className="mt-2 self-start border border-[color:var(--mati-forest)] text-[color:var(--mati-forest)] px-5 py-2.5 rounded-full text-[12px] font-sub font-semibold tracking-wider uppercase"
                >
                  Sign in
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  onJoin?.();
                }}
                data-testid="mobile-join-btn"
                className="mt-2 self-start bg-[color:var(--mati-clay)] text-[color:var(--mati-sand)] px-5 py-2.5 rounded-full text-[12px] font-sub font-semibold tracking-wider uppercase"
              >
                Join the Drive
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
